/*
 * © 2017 Renishaw plc. All rights reserved.
 * This source file is the confidential property and copyright of Renishaw plc
 * Reproduction or transmission in whole or in part, in any form or
 * by any means, electronic, mechanical or otherwise, is prohibited
 * without the prior written consent of the copyright owner.
 */
/* eslint-disable camelcase */
// Important - all imports are importing the same instance of axios and not a new instance each time.
// instance gets mutated with setBase in the app start-up.
import axios, { AxiosRequestConfig, InternalAxiosRequestConfig } from "axios";
import { store, rootActions } from "../store";
import { CMSClient } from "@/cms-api/";
import constants from "@/constants";
import { getAuthentication } from "@/store/sagas/authentication";
import { AuthResponse, isAuthFailure } from "@centralwebteam/narwhal";

const instance = axios.create();
let refreshing: Promise<AuthResponse> | null = null;
let tokenRetries = 0;
let isFailedEndpoints: boolean = false;
let timerId: NodeJS.Timeout | null = null;

const waitForAuth = async (config: AxiosRequestConfig<any>) => {
  // If already refreshing, wait for it to complete
  if (refreshing !== null) {
    try {
      await refreshing;
      // Token refreshed, retry the original request
      return Promise.resolve(instance.request(config));
    } catch (error) {
      return Promise.reject(error);
    }
  }

  // Start a new refresh
  const auth = getAuthentication();
  if (!auth || !auth.refresh_token) {
    store.dispatch(rootActions.session.axiosInterceptorFailedToRefreshToken());
    return Promise.reject(
      "Failed to refresh auth token. No refresh token found in local storage."
    );
  }

  // Double-check we're not already refreshing (race condition protection)
  if (refreshing !== null) {
    try {
      await refreshing;
      return Promise.resolve(instance.request(config));
    } catch (error) {
      return Promise.reject(error);
    }
  }

  try {
    window.isRefreshing = true;
    refreshing = CMSClient.auth.refresh({
      grant_type: "refresh_token",
      client_id: constants.clientId,
      refresh_token: auth.refresh_token,
    }).promise;

    const refreshResolve = await refreshing;

    if (isAuthFailure(refreshResolve)) {
      console.warn(`Auth failure: ${refreshResolve.error}`);
      // Create an error that looks like an axios error for consistent handling
      const authError: any = new Error(
        `Auth refresh failed: ${refreshResolve.error}`
      );
      authError.response = { status: 400 }; // Treat auth failures as 400 Bad Request
      throw authError;
    }

    tokenRetries = 0;

    // Update localStorage immediately with the new token before dispatching to saga
    // This prevents a race condition where the retry request uses the old token
    const currentAuth = getAuthentication();
    if (currentAuth) {
      const expiry = Date.now() + refreshResolve.expires_in * 1000;
      localStorage.setItem(
        constants.authLocalStorageKey,
        JSON.stringify({
          ...refreshResolve,
          username: currentAuth.username,
          expiry,
        })
      );
    }

    // Now dispatch to saga for other side effects (scheduling next refresh, etc.)
    store.dispatch(rootActions.session.userSessionRefreshed(refreshResolve));

    // Token refreshed successfully, retry the original request
    return Promise.resolve(instance.request(config));
  } catch (error) {
    console.warn(`Failed to refresh auth token.`, error);
    throw error;
  } finally {
    refreshing = null;
    window.isRefreshing = false;
  }
};
const handleTimer = () => {
  setTimeout(() => {
    const footerShown = store.getState().manageServer.showFooter;
    if (footerShown && !isFailedEndpoints) {
      store.dispatch(
        rootActions.session.showFooter({
          showFooter: false,
        })
      );
    }
    timerId = null;
  }, 10000);
};

function blockWhilstTokenRefreshProcessing(
  request: InternalAxiosRequestConfig<any>
) {
  const requestIsNotToken = request?.url !== "token";
  return window.isRefreshing && requestIsNotToken;
}

/** Set up all Axios requests with authentication headers */
instance.interceptors.request.use(async (request) => {
  if (blockWhilstTokenRefreshProcessing(request)) {
    // When the token is refreshing we will block any new requests from
    // processing until the refresh has completed - or the time cap of 5
    // second (20 * 0.25) is met.
    const retryLimit = 20;
    const retryIntervalSecs = 0.25;

    for (let i = 0; i < retryLimit; i++) {
      await addNumberOfSecondsDelay(retryIntervalSecs);

      if (!window.isRefreshing) {
        break;
      }
    }
    // Don't forcibly set window.isRefreshing = false here
    // Let the refresh operation manage its own flag
  }

  if (request.url && request.url === "token") {
    // This is a token request so remove Authorization header
    delete request.headers?.Authorization;
  } else {
    try {
      const storedAuth = localStorage.getItem(constants.authLocalStorageKey);

      if (!storedAuth) {
        // Not logged in - request will proceed without auth header
        // If server needs auth, it will return 401 and response interceptor will handle it
        return request;
      }

      const { token_type, access_token } = JSON.parse(storedAuth);
      request.headers.setAuthorization(`${token_type} ${access_token}`);
    } catch (error) {
      console.warn("Failed to set Authorization header");
    }
  }
  return request;
}, undefined);

/*
 * IF 600
 *  log out
 * IF 401
 *  error
 * IF refreshing
 *  wait for refreshing to complete
 * ELSE
 *  attempt refresh
 * IF refresh succeeds
 *  replace err.config auth header with new auth header
 *  return new request using config
 * ELSE
 *   unauthenticate user
 */

/** Handle all Axios responses */
instance.interceptors.response.use(
  /* Successful responses (2xx) */
  async (r: any) => {
    handleTimer();
    isFailedEndpoints = false;

    if (!r.data) return Promise.resolve(r);
    return Promise.resolve(r.data);
  },
  /* Unsuccessful responses (>=400)*/
  async (r) => {
    // Cancelled requests (e.g. a user-cancelled download) are not endpoint failures
    if (axios.isCancel(r)) {
      return Promise.reject(r);
    }

    const status: number | undefined = r.response?.status;
    if (status === undefined || (status >= 500 && status < 600)) {
      isFailedEndpoints = true;
    }

    if (isFailedEndpoints && timerId === null) {
      store.dispatch(
        rootActions.session.showFooter({
          showFooter: true,
        })
      );
      timerId = setTimeout(handleTimer, 10000);
    }

    try {
      if (status === 600) {
        store.dispatch(rootActions.session.serverNotLicensed());
        return Promise.reject("Server is not licensed");
      }
      // login or refresh call
      else if (r?.config?.url === "token") {
        tokenRetries++;

        if (tokenRetries > 9) {
          store.dispatch(rootActions.session.axiosInterceptorTokenCallFailed());
          // Last-ditch attempt to fix by reloading the whole page
          document.location.reload();
          return Promise.reject(
            "Failed to refresh auth token. Reloading the page."
          );
        }
      }

      // Refresh the session if it's expired. Usually, the authentication saga should refresh it before this point.
      if (status === 401) {
        try {
          const retriedResponse = await waitForAuth(r.config);
          // waitForAuth refreshed the token and retried the request successfully
          // Return the successful response
          return retriedResponse;
        } catch (refreshError: any) {
          // Refresh failed - check if it's a legitimate auth failure (400)
          const refreshStatus =
            refreshError?.response?.status || refreshError?.status;
          if (refreshStatus === 400) {
            // 400 on token refresh means invalid/expired refresh token - log out
            console.warn("Refresh token invalid or expired, logging out");
            store.dispatch(rootActions.session.userLoggedOut());
          }
          return Promise.reject(r);
        }
      } else {
        // Not a 401, so there's no quick fix
        return Promise.reject(r);
      }
    } catch (error) {
      // Catch any other errors
      return Promise.reject(r);
    }
  }
);

export function setBase(baseURL: string) {
  instance.defaults.baseURL = baseURL;
}

export default instance;

const addNumberOfSecondsDelay = (n: number) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(true);
    }, n * 1000);
  });
};
